import type { Result } from '../../types/result';
import { SUBMITTED_CTA_LABEL } from '../DegreeCardDetailed/useSubmittedForProgram';

/**
 * The four mutually-exclusive outcomes the Results Page recommender CTA
 * can resolve to. Naming is aligned with the DSL action grammar
 * (`createActionDispatcher`) so EDUN-12917's generic `linkout` action can
 * plug into the same `appCta` seam without re-deriving from imperative
 * component code:
 *
 *   - `explore`   — pre-submission card → opens the governed lead flow.
 *   - `submitted` — already-submitted card under governed routing →
 *                   re-opens the governed lead flow (exit-strategy view).
 *   - `appCta`    — already-submitted card under legacy routing with a
 *                   resolved application URL → links out in a new tab.
 *   - `noop`      — already-submitted card under legacy routing with no
 *                   resolved URL → renders submitted, navigates nowhere.
 */
export type RecommenderCtaIntent = 'explore' | 'appCta' | 'submitted' | 'noop';

/**
 * The governed CTA state for a single recommender card. The `label` and
 * `intent` are resolved together so the rendered copy and the click
 * behavior stay in sync by construction. `url` is only populated for the
 * `appCta` intent (the resolved `applicationCta.url`).
 */
export interface RecommenderCtaState {
  label: string;
  intent: RecommenderCtaIntent;
  url?: string;
}

export interface ResolveRecommenderCtaStateOptions {
  isProgramSubmitted: boolean;
  /**
   * When `true`, an already-submitted card routes back through the
   * governed lead flow (`submitted` intent) instead of the legacy
   * linkout / no-op behavior. See {@link RecommenderCardProps.governSubmittedClick}.
   */
  governSubmittedClick: boolean;
}

const EXPLORE_CTA_LABEL = 'Explore Program';
const APP_CTA_FALLBACK_LABEL = 'Start Your Application';

/**
 * Pure resolver for the Results Page recommender CTA "state machine".
 *
 * Subsumes the previously-imperative `resolveCtaLabel` (label) and
 * `handleExploreClick` (routing branch) into one governed, unit-testable
 * value: given a result and the submitted / governed flags, return the
 * label to render and the intent the click dispatcher must route on.
 *
 * Total and side-effect free — the component owns analytics and
 * navigation; this only decides what should happen.
 */
export function resolveRecommenderCtaState(
  result: Result,
  {
    isProgramSubmitted,
    governSubmittedClick,
  }: ResolveRecommenderCtaStateOptions,
): RecommenderCtaState {
  if (!isProgramSubmitted) {
    return { label: EXPLORE_CTA_LABEL, intent: 'explore' };
  }
  if (governSubmittedClick) {
    return { label: SUBMITTED_CTA_LABEL, intent: 'submitted' };
  }
  const url = result.applicationCtaURL;
  if (url) {
    return {
      label: result.applicationCtaLabel || APP_CTA_FALLBACK_LABEL,
      intent: 'appCta',
      url,
    };
  }
  // Submitted, legacy routing, no resolved URL — render submitted, no-op
  // on click (preserves the legacy submitted-without-URL behavior and the
  // SAD fail-safe principle).
  return { label: SUBMITTED_CTA_LABEL, intent: 'noop' };
}
